/**
 * Icon — kit-independent inline SVG icons.
 * All icons use currentColor and accept an optional className/style for sizing.
 * Usage: <Icon name="spinner" className={styles.spin} />
 */
import React from 'react';

export type IconName =
    | 'spinner'
    | 'check-circle'
    | 'times-circle'
    | 'lock'
    | 'bullseye'
    | 'eye'
    | 'edit'
    | 'trash'
    | 'user-plus'
    | 'user-check'
    | 'sliders'
    | 'twitter'
    | 'plus'
    | 'check'
    | 'chevron-down'
    | 'chevron-up'
    | 'building'
    | 'search';

interface IconProps {
    name: IconName;
    className?: string;
    style?: React.CSSProperties;
    id?: string;
    onClick?: React.MouseEventHandler<SVGSVGElement>;
    'aria-hidden'?: boolean;
    'aria-label'?: string;
}

const paths: Record<IconName, React.ReactElement> = {
    spinner: (
        <circle
            cx="12" cy="12" r="10"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="31.4 62.8"
            strokeLinecap="round"
        />
    ),
    'check-circle': (
        <>
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
            <polyline points="7 12 10.5 15.5 17 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </>
    ),
    'times-circle': (
        <>
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
            <line x1="8" y1="8" x2="16" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="16" y1="8" x2="8" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
    lock: (
        <>
            <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 11V7a4 4 0 0 1 8 0v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
    bullseye: (
        <>
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
            <circle cx="12" cy="12" r="6"  fill="none" stroke="currentColor" strokeWidth="2"/>
            <circle cx="12" cy="12" r="2"  fill="currentColor"/>
        </>
    ),
    eye: (
        <>
            <path d="M1 12S5 5 12 5s11 7 11 7-4 7-11 7S1 12 1 12Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2"/>
        </>
    ),
    edit: (
        <>
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </>
    ),
    trash: (
        <>
            <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
    'user-plus': (
        <>
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
            <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="17" y1="11" x2="23" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
    'user-check': (
        <>
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
            <polyline points="17 11 19 13 23 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </>
    ),
    sliders: (
        <>
            <line x1="4"  y1="6"  x2="20" y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="4"  y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="4"  y1="18" x2="20" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="8"  cy="6"  r="2" fill="white" stroke="currentColor" strokeWidth="2"/>
            <circle cx="16" cy="12" r="2" fill="white" stroke="currentColor" strokeWidth="2"/>
            <circle cx="10" cy="18" r="2" fill="white" stroke="currentColor" strokeWidth="2"/>
        </>
    ),
    twitter: (
        <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53A4.48 4.48 0 0 0 22.43.36a9 9 0 0 1-2.88 1.1A4.52 4.52 0 0 0 16.11 0c-2.5 0-4.52 2.02-4.52 4.52 0 .35.04.7.11 1.03C7.69 5.37 4.07 3.58 1.64.9a4.52 4.52 0 0 0-.61 2.27c0 1.57.8 2.95 2.01 3.76a4.5 4.5 0 0 1-2.05-.57v.06c0 2.19 1.56 4.02 3.63 4.43a4.54 4.54 0 0 1-2.04.08 4.53 4.53 0 0 0 4.23 3.14A9.07 9.07 0 0 1 0 15.54a12.8 12.8 0 0 0 6.92 2.03c8.3 0 12.85-6.88 12.85-12.85 0-.2 0-.39-.01-.58A9.17 9.17 0 0 0 23 3Z" fill="currentColor"/>
    ),
    plus: (
        <>
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
    check: (
        <polyline points="4 12 9 17 20 7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    ),
    'chevron-down': (
        <polyline points="6 9 12 15 18 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    ),
    'chevron-up': (
        <polyline points="18 15 12 9 6 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    ),
    building: (
        <>
            <rect x="3" y="3" width="18" height="18" rx="1" fill="none" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2"/>
            <line x1="9" y1="21" x2="9" y2="9" stroke="currentColor" strokeWidth="2"/>
            <rect x="13" y="13" width="3" height="3" fill="currentColor"/>
            <rect x="13" y="17" width="3" height="4" fill="currentColor"/>
        </>
    ),
    search: (
        <>
            <circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </>
    ),
};

export default function Icon({ name, className, style, id, onClick, ...rest }: IconProps) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            className={className}
            style={{ cursor: onClick ? 'pointer' : undefined, ...style }}
            id={id}
            onClick={onClick}
            aria-hidden={rest['aria-hidden'] ?? true}
            aria-label={rest['aria-label']}
            focusable="false"
        >
            {paths[name]}
        </svg>
    );
}
